<script setup lang="ts">
  import { computed, type CSSProperties } from 'vue';
  import type { CompiledFieldNode, FieldValue, SelectOption } from '@record-fill';
  import type { RadioGroupProperties } from '../../../model/form.types';

  const props = defineProps<{ node: CompiledFieldNode; disabled: boolean; readOnly?: boolean; inputClass?: string }>();
  const emit = defineEmits<{ (e: 'update', value: FieldValue): void }>();

  const properties = computed(() => props.node.element.properties as RadioGroupProperties);
  const options = computed<SelectOption[]>(() => (props.node.control.kind === 'radio' ? props.node.control.options : []));
  const isHorizontal = computed(() => properties.value.orientation === 'horizontal');
  const selected = computed(() => (typeof props.node.value === 'string' ? props.node.value : ''));

  // Same option typography rules as RadioGroupElement.vue in the builder.
  const optionStyles = computed<CSSProperties>(() => {
    const p = properties.value;
    const styles: CSSProperties = { fontSize: p.optionFontSize ? `${p.optionFontSize}px` : '10px', lineHeight: '1' };
    if (p.optionFontWeight === 'bold') styles.fontWeight = '700';
    else if (p.optionFontWeight === 'semibold') styles.fontWeight = '600';
    else if (p.optionFontWeight === 'normal') styles.fontWeight = '400';
    if (p.optionFontStyle === 'italic') styles.fontStyle = 'italic';
    if (p.optionTextDecoration && p.optionTextDecoration !== 'none') {
      styles.textDecoration = p.optionTextDecoration;
      styles.textDecorationSkipInk = 'none';
    }
    if (p.optionTextAlign) styles.textAlign = p.optionTextAlign;
    if (p.optionColor) styles.color = p.optionColor;
    return styles;
  });

  // Same reason as the checkbox: a radio has no readonly attribute and a disabled one is faded.
  function blockWhenReadOnly(event: Event): void {
    if (props.readOnly) event.preventDefault();
  }

  function select(value: string): void {
    if (!props.readOnly) emit('update', value);
  }
</script>

<template>
  <div :data-field-id="node.id">
    <div
      role="radiogroup"
      :aria-label="node.label"
      :class="isHorizontal ? 'flex flex-wrap gap-2' : 'flex flex-col gap-1'"
    >
      <label
        v-for="option in options"
        :key="option.value"
        class="inline-flex items-center gap-1.5 leading-none"
        :class="{ 'cursor-not-allowed opacity-60': disabled, 'cursor-default': readOnly, 'cursor-pointer': !disabled && !readOnly }"
      >
        <input
          type="radio"
          class="size-3.5 shrink-0 align-middle accent-[var(--theme-secondary)]"
          :name="`radio-${node.id}`"
          :value="option.value"
          :checked="selected === option.value"
          :disabled="disabled"
          :tabindex="readOnly ? -1 : undefined"
          @click="blockWhenReadOnly"
          @change="select(option.value)"
        >
        <span
          class="select-none text-left font-medium text-gray-07"
          :style="optionStyles"
        >{{ option.label }}</span>
      </label>
    </div>
  </div>
</template>
